/**
 * `@spatial/theme-kitchen`: the Spatial kitchen storefront theme.
 *
 * <p>Registered under theme id {@link THEME_ID} (`spatial-kitchen`). A
 * storefront picks it up through the extension hook in PLAN.md Contract 3
 * (`@spatial/storefront-extension`); nothing in commerce imports this package.
 *
 * <h3>What the bundle contains</h3>
 * <ul>
 *   <li><b>Kitchen blocks</b>: `SpatialKitchen*`, the sections of the design
 *       brief. Their registry definitions live in
 *       `templates/seed-data/blocks/`.</li>
 *   <li><b>Chrome</b>: an override of the host `SiteShell` block that frames
 *       the host shell with this theme's header and footer (see
 *       `chrome/SpatialKitchenSiteShell.tsx` for why), plus `header`/`footer`
 *       resolvers for hosts that pass `resolveThemedHeader` to the shell
 *       presenter (for example an admin shell preview).</li>
 *   <li><b>Functional skins</b>: pass-through overrides that restyle the
 *       platform's basket, checkout, account, search and form blocks without
 *       forking them (`functional/withKitchenSkin.tsx`).</li>
 * </ul>
 */
import type { BlockComponent, RegistryThemeBundle } from '@akademi/site-builder-runtime';
import { createElement } from 'react';
import { THEME_ID } from './brand';
import { SpatialKitchenAccountTypes } from './blocks/SpatialKitchenAccountTypes';
import { SpatialKitchenCabinetBuilder } from './blocks/SpatialKitchenCabinetBuilder';
import { SpatialKitchenCategoryTiles } from './blocks/SpatialKitchenCategoryTiles';
import { SpatialKitchenCatalogue } from './blocks/SpatialKitchenCatalogue';
import { SpatialKitchenDoorBuilder } from './blocks/SpatialKitchenDoorBuilder';
import { SpatialKitchenFaq } from './blocks/SpatialKitchenFaq';
import { SpatialKitchenFitterCoverage } from './blocks/SpatialKitchenFitterCoverage';
import { SpatialKitchenJobBoard } from './blocks/SpatialKitchenJobBoard';
import { SpatialKitchenJourneys } from './blocks/SpatialKitchenJourneys';
import { SpatialKitchenMyDesigns } from './blocks/SpatialKitchenMyDesigns';
import { SpatialKitchenPlannerStart } from './blocks/SpatialKitchenPlannerStart';
import { SpatialKitchenSceneHero } from './blocks/SpatialKitchenSceneHero';
import { SpatialKitchenSiteSchema } from './blocks/SpatialKitchenSiteSchema';
import { SpatialKitchenSplit } from './blocks/SpatialKitchenSplit';
import { SpatialKitchenStyleCards } from './blocks/SpatialKitchenStyleCards';
import { SpatialKitchenUspStrip } from './blocks/SpatialKitchenUspStrip';
import { SpatialKitchenColourSwatches } from './blocks/SpatialKitchenColourSwatches';
import { SpatialKitchenCtaPair } from './blocks/SpatialKitchenCtaPair';
import { SpatialKitchenDoorStyles } from './blocks/SpatialKitchenDoorStyles';
import { SpatialKitchenFeaturedRanges } from './blocks/SpatialKitchenFeaturedRanges';
import { SpatialKitchenFeatureRows } from './blocks/SpatialKitchenFeatureRows';
import { SpatialKitchenFooter } from './blocks/SpatialKitchenFooter';
import { SpatialKitchenHero } from './blocks/SpatialKitchenHero';
import { SpatialKitchenInspiration } from './blocks/SpatialKitchenInspiration';
import { SpatialKitchenPageIntro } from './blocks/SpatialKitchenPageIntro';
import { SpatialKitchenSteps } from './blocks/SpatialKitchenSteps';
import { SpatialKitchenStories } from './blocks/SpatialKitchenStories';
import { SpatialKitchenTrustFigures } from './blocks/SpatialKitchenTrustFigures';
import { footerPropsFromShell, headerPropsFromShell } from './chrome/shellMapping';
import { SpatialKitchenHeader } from './chrome/SpatialKitchenHeader';
import { SpatialKitchenSiteShell } from './chrome/SpatialKitchenSiteShell';
import { kitchenSkinOverrides } from './functional/kitchenSkinOverrides';

export { BRAND_NAME, PLANNER_HREF, TEMPLATE_KEY, THEME_ID } from './brand';
export { palette, contrastRatio, relativeLuminance } from './styles/palette';
export { SpatialKitchenHero, type SpatialKitchenHeroProps } from './blocks/SpatialKitchenHero';
export { SpatialKitchenFeatureRows, type SpatialKitchenFeatureRowsProps } from './blocks/SpatialKitchenFeatureRows';
export { SpatialKitchenCtaPair, type SpatialKitchenCtaPairProps } from './blocks/SpatialKitchenCtaPair';
export {
  SpatialKitchenColourSwatches,
  type SpatialKitchenColourSwatchesProps,
} from './blocks/SpatialKitchenColourSwatches';
export {
  SpatialKitchenCategoryTiles,
  type SpatialKitchenCategoryTilesProps,
} from './blocks/SpatialKitchenCategoryTiles';
export {
  SpatialKitchenFeaturedRanges,
  type SpatialKitchenFeaturedRangesProps,
} from './blocks/SpatialKitchenFeaturedRanges';
export { SpatialKitchenStories, type SpatialKitchenStoriesProps } from './blocks/SpatialKitchenStories';
export { SpatialKitchenTrustFigures, type SpatialKitchenTrustFiguresProps } from './blocks/SpatialKitchenTrustFigures';
export { SpatialKitchenInspiration, type SpatialKitchenInspirationProps } from './blocks/SpatialKitchenInspiration';
export { SpatialKitchenFooter, type SpatialKitchenFooterProps } from './blocks/SpatialKitchenFooter';
export { SpatialKitchenPageIntro, type SpatialKitchenPageIntroProps } from './blocks/SpatialKitchenPageIntro';
export { SpatialKitchenSteps, type SpatialKitchenStepsProps } from './blocks/SpatialKitchenSteps';
export { SpatialKitchenDoorStyles, type SpatialKitchenDoorStylesProps } from './blocks/SpatialKitchenDoorStyles';
export { SpatialKitchenHeader, type SpatialKitchenHeaderProps } from './chrome/SpatialKitchenHeader';
export { SpatialKitchenSiteShell } from './chrome/SpatialKitchenSiteShell';
export { headerPropsFromShell, footerPropsFromShell } from './chrome/shellMapping';
export { KitchenSkin, type KitchenSkinProps } from './functional/withKitchenSkin';
export { SKINNED_BLOCKS, type SkinWidth } from './functional/skinnedBlocks';
export { kitchenSkinOverrides } from './functional/kitchenSkinOverrides';
export { KitchenCatalogueSurface } from './functional/KitchenCatalogueSurface';
export { SpatialKitchenCatalogue, type SpatialKitchenCatalogueProps } from './blocks/SpatialKitchenCatalogue';
export { SpatialKitchenDoorBuilder, type SpatialKitchenDoorBuilderProps } from './blocks/SpatialKitchenDoorBuilder';
export {
  SpatialKitchenCabinetBuilder,
  type SpatialKitchenCabinetBuilderProps,
} from './blocks/SpatialKitchenCabinetBuilder';
export { SpatialPortsProvider, useSpatialPorts, type SpatialPorts, type DoorBasket, type CabinetBasket } from './data/ports';
export type { CabinetCatalogue, CabinetProduct } from './data/cabinetCatalogue';
export type { CabinetOrderSpec, CabinetPricing, CabinetQuote } from './data/cabinetPricing';
export { SpatialKitchenPlannerStart, type SpatialKitchenPlannerStartProps } from './blocks/SpatialKitchenPlannerStart';
export { decodeStartBrief, encodeStartBrief, startHref, type StartBrief } from './planner/startBrief';
export type { TradeJobs } from './data/tradeJobs';
export { SpatialKitchenMyDesigns, type SpatialKitchenMyDesignsProps } from './blocks/SpatialKitchenMyDesigns';
export type { DoorCatalogue, DoorCollection, DoorColour, DoorRange } from './data/doorCatalogue';
export type { DoorOrderSpec, DoorPricing, DoorQuote } from './data/doorPricing';
export type { SavedDesign, SavedDesigns } from './data/savedDesigns';

const block = (component: unknown) => component as BlockComponent;

/**
 * The block types this theme ADDS. Each has a registry definition in
 * `templates/seed-data/blocks/<type>.json`; a test keeps the two in step.
 */
export const spatialKitchenBlocks: Record<string, BlockComponent> = {
  SpatialKitchenCatalogue: block(SpatialKitchenCatalogue),
  SpatialKitchenDoorBuilder: block(SpatialKitchenDoorBuilder),
  SpatialKitchenCabinetBuilder: block(SpatialKitchenCabinetBuilder),
  SpatialKitchenPlannerStart: block(SpatialKitchenPlannerStart),
  SpatialKitchenAccountTypes: block(SpatialKitchenAccountTypes),
  SpatialKitchenFitterCoverage: block(SpatialKitchenFitterCoverage),
  SpatialKitchenJobBoard: block(SpatialKitchenJobBoard),
  SpatialKitchenMyDesigns: block(SpatialKitchenMyDesigns),
  SpatialKitchenSceneHero: block(SpatialKitchenSceneHero),
  SpatialKitchenJourneys: block(SpatialKitchenJourneys),
  SpatialKitchenUspStrip: block(SpatialKitchenUspStrip),
  SpatialKitchenStyleCards: block(SpatialKitchenStyleCards),
  SpatialKitchenSplit: block(SpatialKitchenSplit),
  SpatialKitchenFaq: block(SpatialKitchenFaq),
  SpatialKitchenSiteSchema: block(SpatialKitchenSiteSchema),
  SpatialKitchenHero: block(SpatialKitchenHero),
  SpatialKitchenFeatureRows: block(SpatialKitchenFeatureRows),
  SpatialKitchenCtaPair: block(SpatialKitchenCtaPair),
  SpatialKitchenColourSwatches: block(SpatialKitchenColourSwatches),
  SpatialKitchenCategoryTiles: block(SpatialKitchenCategoryTiles),
  SpatialKitchenFeaturedRanges: block(SpatialKitchenFeaturedRanges),
  SpatialKitchenStories: block(SpatialKitchenStories),
  SpatialKitchenTrustFigures: block(SpatialKitchenTrustFigures),
  SpatialKitchenInspiration: block(SpatialKitchenInspiration),
  SpatialKitchenFooter: block(SpatialKitchenFooter),
  SpatialKitchenPageIntro: block(SpatialKitchenPageIntro),
  SpatialKitchenSteps: block(SpatialKitchenSteps),
  SpatialKitchenDoorStyles: block(SpatialKitchenDoorStyles),
};

export const spatialKitchenTheme: RegistryThemeBundle = {
  blocks: {
    ...spatialKitchenBlocks,
    ...kitchenSkinOverrides,
    SiteShell: block(SpatialKitchenSiteShell),
  },
  header: (style, props) =>
    style === THEME_ID
      ? createElement(SpatialKitchenHeader, headerPropsFromShell(props.config))
      : null,
  footer: (style, props) =>
    style === THEME_ID
      ? createElement(SpatialKitchenFooter, footerPropsFromShell(props.config))
      : null,
};

export default spatialKitchenTheme;
